import React from 'react';
import SectionLabel from './SectionLabel';

const alignmentPillars = [
    'Visibility', 'Positioning', 'Customer Experience',
    'Operations', 'Conversion', 'Long-term Growth',
];

const storylabPillars = [
    'Creative Direction', 'Brand Storytelling',
    'Content Systems', 'Cinematic Brand Comms',
];

const Founder: React.FC = () => (
    <section
        id="founder"
        className="relative py-[120px] px-6 md:px-10"
        style={{
            background: 'linear-gradient(158deg, #faf4e8 0%, #f5ecce 38%, #ede0b4 72%, #e8d89e 100%)',
            borderTop: '1px solid rgba(184,154,90,0.18)',
            borderBottom: '1px solid rgba(184,154,90,0.18)',
        }}
    >
        {/* Ambient glow */}
        <div
            className="absolute pointer-events-none"
            style={{
                bottom: -80, left: -60,
                width: 420, height: 420,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(184,154,90,0.1) 0%, transparent 70%)',
            }}
        />

        <div className="max-w-[1200px] mx-auto relative z-10">
            <SectionLabel>Founder</SectionLabel>

            {/* ── Founder identity block — no photo ── */}
            <div
                className="flex flex-col md:flex-row md:items-end justify-between gap-6 mt-10 pb-10 mb-12"
                style={{ borderBottom: '1px solid rgba(184,154,90,0.2)' }}
            >
                <div>
                    <h2
                        style={{
                            fontFamily: '"Cormorant Garamond", Georgia, serif',
                            fontSize: 'clamp(36px, 5vw, 56px)',
                            fontWeight: 500,
                            color: '#1a130a',
                            letterSpacing: '0.02em',
                            lineHeight: 1.05,
                            marginBottom: 10,
                        }}
                    >
                        Sharon Ayeni
                    </h2>
                    <p
                        style={{
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: 10, fontWeight: 400,
                            color: '#8a7040', letterSpacing: '0.14em',
                            lineHeight: 1.9,
                        }}
                    >
                        Operations Manager &nbsp;·&nbsp; Digital Marketing Specialist &nbsp;·&nbsp; Founder
                    </p>
                </div>

                {/* Brand tags */}
                <div className="flex flex-wrap gap-3">
                    {['HNL Digital Luxe', 'HNL StoryLab'].map((b) => (
                        <span
                            key={b}
                            style={{
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: 9, fontWeight: 600,
                                letterSpacing: '0.2em', textTransform: 'uppercase',
                                color: '#8a7040',
                                border: '1px solid rgba(184,154,90,0.35)',
                                padding: '7px 14px',
                                display: 'inline-block',
                            }}
                        >
                            {b}
                        </span>
                    ))}
                </div>
            </div>

            {/* ── Two-column editorial layout ── */}
            <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 64 }}>

                {/* LEFT column */}
                <div>
                    {/* Pull quote */}
                    <blockquote
                        style={{
                            fontFamily: '"Cormorant Garamond", Georgia, serif',
                            fontSize: 'clamp(20px, 2.4vw, 28px)',
                            fontWeight: 300, fontStyle: 'italic',
                            color: '#1a130a', lineHeight: 1.4,
                            paddingLeft: 20,
                            borderLeft: '2px solid #b89a5a',
                            marginBottom: 36,
                        }}
                    >
                        HNL Digital Luxe was built after seeing how often brands mistake activity for structure.
                    </blockquote>

                    {[
                        'HNL Digital Luxe did not begin as a traditional agency idea. It began from repeatedly seeing the same problem across brands, businesses, and growing companies.',
                        'The marketing looked active. The business looked busy. Content was going out consistently. Ads were running. People were watching. But internally, nothing was truly connected. The positioning was unclear. The systems were inconsistent. The customer journey felt fragmented. Operations struggled to support growth. Teams were reacting instead of executing strategically.',
                        'Over time, it became obvious that many brands did not simply need "more marketing." They needed structure behind the visibility. That understanding became the foundation of HNL Digital Luxe.',
                    ].map((text, i) => (
                        <p key={i} style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 300, color: '#4a3e2a', lineHeight: 2.1, letterSpacing: '0.02em', marginBottom: 20 }}>
                            {text}
                        </p>
                    ))}
                </div>

                {/* RIGHT column */}
                <div>
                    <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 300, color: '#4a3e2a', lineHeight: 2.1, letterSpacing: '0.02em', marginBottom: 20 }}>
                        What started as a passion for digital marketing, creative direction, business operations, branding, and growth strategy evolved into a systems-led company focused on helping brands operate more intentionally — not just appear more visible online.
                    </p>

                    {/* Section divider */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '28px 0' }}>
                        <span style={{ flex: 1, height: 1, background: 'rgba(184,154,90,0.25)' }} />
                        <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.26em', textTransform: 'uppercase', color: '#8a7040', whiteSpace: 'nowrap' }}>
                            The Alignment Framework
                        </span>
                        <span style={{ flex: 1, height: 1, background: 'rgba(184,154,90,0.25)' }} />
                    </div>

                    <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 300, color: '#4a3e2a', lineHeight: 2.1, letterSpacing: '0.02em', marginBottom: 18 }}>
                        The goal is to create alignment between:
                    </p>

                    {/* Pillars grid */}
                    <div className="grid grid-cols-2" style={{ gap: 8, marginBottom: 32 }}>
                        {alignmentPillars.map((p) => (
                            <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 13px', background: 'rgba(255,248,225,0.5)', border: '1px solid rgba(184,154,90,0.18)' }}>
                                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#b89a5a', flexShrink: 0 }} />
                                <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 11, fontWeight: 400, color: '#4a3e2a', letterSpacing: '0.03em' }}>{p}</span>
                            </div>
                        ))}
                    </div>

                    {/* StoryLab callout */}
                    <div style={{ padding: '24px 24px', background: 'rgba(255,248,225,0.45)', border: '1px solid rgba(184,154,90,0.28)', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #b89a5a, #d4b87a 60%, transparent)' }} />
                        <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8.5, fontWeight: 600, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#b89a5a', marginBottom: 8 }}>The Creative Arm</div>
                        <h3 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 20, fontWeight: 400, color: '#1a130a', marginBottom: 12 }}>HNL StoryLab</h3>
                        <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, fontWeight: 300, color: '#5a4e38', lineHeight: 1.9, letterSpacing: '0.02em', marginBottom: 14 }}>
                            The visual storytelling and content production arm of the brand — bridging the gap between strategy and visual execution. Strong creative direction, emotionally intelligent storytelling, and content that feels intentional, not disposable.
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                            {storylabPillars.map((p) => (
                                <span key={p} style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8a7040', border: '1px solid rgba(184,154,90,0.28)', padding: '4px 10px' }}>{p}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Closing philosophy — full width */}
            <div style={{ marginTop: 56, paddingTop: 32, borderTop: '1px solid rgba(184,154,90,0.2)', textAlign: 'center' }}>
                <p style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 'clamp(18px, 2.2vw, 24px)', fontWeight: 300, fontStyle: 'italic', color: '#6b5030', lineHeight: 1.6, letterSpacing: '0.01em', maxWidth: 640, margin: '0 auto' }}>
                    Growth becomes more powerful when the structure behind the brand is built intentionally.
                </p>
            </div>
        </div>
    </section>
);

export default Founder;